import * as React from "react";
import styled from "styled-components";

import bgImg from "~/assets/gfx/module1-bg3.jpg";
import { ReactComponent as CloseIcon } from "~/assets/svg/closeIcon.svg";

export const Hub_ThreeCircles: React.FC<Record<string, never>> = () => {
  const [activeCircle, setActiveCircle] = React.useState<number>(-1);

  const handleClick = (id: number) => {
    return id === activeCircle ? setActiveCircle(-1) : setActiveCircle(id);
  };

  const threeCirclesData = [
    {
      title: "A health and wellbeing policy",
      description: (
        <small>
          A policy is a written document setting out an organisation’s
          commitment to the health and wellbeing of staff. It outlines the
          strategies that will be used to promote health within the workplace. A
          policy will also help ensure the sustainability of the program even
          with staffing changes.
        </small>
      ),
    },
    {
      title: "An environment that supports people to be healthy",
      description: (
        <small>
          The physical workplace environment can either support or present a
          barrier for staff to be healthy. Examples of environmental changes
          that will promote employee health include: providing bicycle storage
          and end-of-trip facilities (e.g. showers and lockers), sit-stand
          workstations, a kitchen equipped with a fridge and basic food
          preparation equipment, not providing alcohol at staff functions, and
          displaying smoke-free signs in outdoor areas and around the perimeter
          of the building.
        </small>
      ),
    },
    {
      title: "Staff education and activities",
      description: (
        <small>
          Education involves providing employees with information on the risks
          associated with unhealthy behaviours and how to be healthier. Examples
          include ‘take the stairs’ posters, nutrition education sessions, hard
          copy resources like recipe books, or information about stretching
          exercises provided on the intranet. Workplace activities like walking
          groups, healthy cooking classes or a 30-day no alcohol challenge
          provide concrete opportunities for those looking to make a change.
        </small>
      ),
    },
  ];

  return (
    <Wrapper>
      <div className="bg-overlay-blueberry-dark text-white px-4 py-lg-3 text-center">
        <ThreeCircles>
          {threeCirclesData.map(({ title, description }, idx) => (
            <CircleContainer key={idx} active={idx === activeCircle}>
              <Circle
                active={idx === activeCircle}
                onClick={() => handleClick(idx)}
              >
                <h3 className="text-gold">{title}</h3>
                {description}
                {idx === activeCircle && <CloseIcon width={20} height={20} />}
              </Circle>
            </CircleContainer>
          ))}
        </ThreeCircles>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  margin: 0 -2rem;
  background: url("${bgImg}");
  background-position: center center;
  background-size: cover;
  @media screen and (min-width: 768px) {
    margin: 0;
  }
`;

const ThreeCircles = styled.div`
  position: relative;
  height: 900px;
  @media screen and (min-width: 768px) {
    height: 600px;
  }
`;

const CircleContainer = styled.div<{ active: boolean }>`
  position: absolute;
  display: block;
  top: 0;
  left: 0;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  transition: transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: ${(p) => (p.active ? "scale(1.5)" : "scale(1)")};
  z-index: ${(p) => (p.active ? 2 : 1)};
  &:nth-of-type(1) {
    top: 50px;
    left: calc(50% - 150px);
    transform-origin: top center;
  }
  &:nth-of-type(2) {
    top: 300px;
    left: calc(50% - 150px);
    transform-origin: center center;
  }
  &:nth-of-type(3) {
    top: 550px;
    left: calc(50% - 150px);
    transform-origin: bottom center;
  }
  @media screen and (min-width: 768px) {
    &:nth-of-type(2) {
      top: 250px;
      left: calc(50% - 275px);
      transform-origin: bottom left;
    }
    &:nth-of-type(3) {
      top: 250px;
      left: calc(50% - 25px);
      transform-origin: bottom right;
    }
  }
`;

const Circle = styled.button<{ active: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem 2.5rem;
  overflow: hidden;
  display: block;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: ${(p) => (p.active ? "space-evenly" : "flex-start")};
  align-items: center;
  margin: auto;
  background: ${(p) => (p.active ? "#004C97" : "rgba(255, 255, 255, 0.2)")};
  transition: all 600ms cubic-bezier(0.34, 1.56, 0.64, 1);
  border: none;
  &:focus {
    outline: none;
  }
  & h3 {
    transition: all 600ms cubic-bezier(0.34, 1.56, 0.64, 1);
    font-size: ${(p) => (p.active ? "1rem" : "1.125rem")};
    color: ${(p) => (p.active ? "white" : "black")};
    margin: ${(p) => (p.active ? "0 1rem 0" : "45% 1rem 0")};
  }
  & small {
    transition: all 400ms cubic-bezier(0.34, 1.56, 0.64, 1);
    color: ${(p) => (p.active ? "white" : "transparent")};
    font-size: 0.7rem;
    line-height: 1.25;
  }
  & svg {
    fill: white;
  }
  @media screen and (min-width: 768px) {
    padding: 1rem 2rem;
  }
`;
